import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Matches any class part value — used as a catch-all validator for CDS token names.
 * tailwind-merge calls this after matching the key prefix (e.g. `cds-bg`),
 * so returning true means "any token name after the prefix is valid".
 */
var isCdsValue = function isCdsValue() {
  return true;
};

/**
 * Excludes directional suffixes (x-, y-, t-, r-, b-, l-) so that
 * e.g. `cds-p-5` matches the `cds-p` group but `cds-px-5`
 * does NOT (it should match `cds-px` instead).
 */
var isNonDirectional = function isNonDirectional(v) {
  return !/^[xytrlb]-/.test(v);
};

/**
 * For deprecated `cds-border-*`: border color tokens use semantic names (e.g. `default`, `brand`),
 * while border width tokens use numeric keys (e.g. `16`, `1`).
 * This validator matches only color tokens by excluding numeric and directional values.
 */
var isBorderColorToken = function isBorderColorToken(v) {
  return !/^[xytrlb]-/.test(v) && !/^\d/.test(v) && !v.startsWith('spacing-');
};
var customTwMerge = /*#__PURE__*/extendTailwindMerge({
  prefix: 'tw-',
  classGroups: {
    // --- Semantic color tokens ---
    'cds-bg': [{
      'cds-bg': [isCdsValue]
    }],
    'cds-text': [{
      'cds-text': [isCdsValue]
    }],
    // Matches both deprecated tw-cds-border-default and canonical tw-cds-border-color-default
    'cds-border-color': [{
      'cds-border': [isBorderColorToken]
    }, {
      'cds-border-color': [isCdsValue]
    }],
    // --- Border radius ---
    // Matches both deprecated tw-cds-radius-s and canonical tw-cds-rounded-s
    'cds-radius': [{
      'cds-radius': [isCdsValue]
    }, {
      'cds-rounded': [isCdsValue]
    }],
    // --- Semantic named padding ---
    // tw-cds-padding-card-s, tw-cds-padding-card-m, etc. → padding
    'cds-padding': [{
      'cds-padding': [isCdsValue]
    }],
    // --- Spacing: margin ---
    'cds-m': [{
      'cds-m': [isNonDirectional]
    }],
    'cds-mx': [{
      'cds-mx': [isCdsValue]
    }],
    'cds-my': [{
      'cds-my': [isCdsValue]
    }],
    'cds-mt': [{
      'cds-mt': [isCdsValue]
    }],
    'cds-mr': [{
      'cds-mr': [isCdsValue]
    }],
    'cds-mb': [{
      'cds-mb': [isCdsValue]
    }],
    'cds-ml': [{
      'cds-ml': [isCdsValue]
    }],
    // --- Spacing: padding ---
    'cds-p': [{
      'cds-p': [isNonDirectional]
    }],
    'cds-px': [{
      'cds-px': [isCdsValue]
    }],
    'cds-py': [{
      'cds-py': [isCdsValue]
    }],
    'cds-pt': [{
      'cds-pt': [isCdsValue]
    }],
    'cds-pr': [{
      'cds-pr': [isCdsValue]
    }],
    'cds-pb': [{
      'cds-pb': [isCdsValue]
    }],
    'cds-pl': [{
      'cds-pl': [isCdsValue]
    }],
    // --- Spacing: gap ---
    'cds-gap': [{
      'cds-gap': [isNonDirectional]
    }],
    'cds-gap-x': [{
      'cds-gap-x': [isCdsValue]
    }],
    'cds-gap-y': [{
      'cds-gap-y': [isCdsValue]
    }]
  },
  conflictingClassGroups: {
    // CDS bg ↔ standard bg-color
    'cds-bg': ['bg-color'],
    'bg-color': ['cds-bg'],
    // CDS text ↔ standard text-color
    'cds-text': ['text-color'],
    'text-color': ['cds-text'],
    // CDS border-color ↔ standard border-color
    'cds-border-color': ['border-color'],
    'border-color': ['cds-border-color'],
    // CDS radius/rounded ↔ standard rounded
    'cds-radius': ['rounded'],
    rounded: ['cds-radius'],
    // CDS named padding (padding-card-*) overrides all standard + CDS padding
    'cds-padding': ['p', 'px', 'py', 'pt', 'pr', 'pb', 'pl', 'cds-p', 'cds-px', 'cds-py', 'cds-pt', 'cds-pr', 'cds-pb', 'cds-pl'],
    // CDS margin ↔ standard margin
    'cds-m': ['m', 'mx', 'my', 'mt', 'mr', 'mb', 'ml'],
    m: ['cds-m', 'cds-mx', 'cds-my', 'cds-mt', 'cds-mr', 'cds-mb', 'cds-ml'],
    'cds-mx': ['mx', 'mr', 'ml'],
    mx: ['cds-mx'],
    'cds-my': ['my', 'mt', 'mb'],
    my: ['cds-my'],
    'cds-mt': ['mt'],
    mt: ['cds-mt'],
    'cds-mr': ['mr'],
    mr: ['cds-mr'],
    'cds-mb': ['mb'],
    mb: ['cds-mb'],
    'cds-ml': ['ml'],
    ml: ['cds-ml'],
    // CDS padding ↔ standard padding
    'cds-p': ['p', 'px', 'py', 'pt', 'pr', 'pb', 'pl'],
    p: ['cds-p', 'cds-px', 'cds-py', 'cds-pt', 'cds-pr', 'cds-pb', 'cds-pl', 'cds-padding'],
    'cds-px': ['px', 'pr', 'pl'],
    px: ['cds-px'],
    'cds-py': ['py', 'pt', 'pb'],
    py: ['cds-py'],
    'cds-pt': ['pt'],
    pt: ['cds-pt'],
    'cds-pr': ['pr'],
    pr: ['cds-pr'],
    'cds-pb': ['pb'],
    pb: ['cds-pb'],
    'cds-pl': ['pl'],
    pl: ['cds-pl'],
    // CDS gap ↔ standard gap
    'cds-gap': ['gap', 'gap-x', 'gap-y'],
    gap: ['cds-gap'],
    'cds-gap-x': ['gap-x'],
    'gap-x': ['cds-gap-x'],
    'cds-gap-y': ['gap-y'],
    'gap-y': ['cds-gap-y']
  }
});
export function cn() {
  for (var _len = arguments.length, inputs = new Array(_len), _key = 0; _key < _len; _key++) {
    inputs[_key] = arguments[_key];
  }
  return customTwMerge(clsx(inputs));
}
export function getFromNestedObject(obj, path) {
  var index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var key = path[index];

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!key) return '';
  var result = obj[key];

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!result) return '';

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (typeof result === 'string') return result;
  return getFromNestedObject(Object(result), path, index + 1);
}